import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useResizableTables } from './useResizableTables';

interface MarkdownContentProps {
    html: string; // sanitized (DOMPurify'd) markdown HTML
    className?: string;
    style?: React.CSSProperties;
}

interface ExpandedTable {
    title: string;
    html: string;
}

// The caption preceding a table becomes the modal title.
const findTableTitle = (table: HTMLTableElement): string => {
    const block = table.closest('.fl-table-block') ?? table;
    let node = block.previousElementSibling;
    while (node) {
        const text = node.textContent?.trim() ?? '';
        if (text) {
            return text;
        }
        node = node.previousElementSibling;
    }
    return 'Table';
};

// Renders sanitized markdown HTML and upgrades its tables (resizable columns +
// expand-to-modal).
export const MarkdownContent = (p: MarkdownContentProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const modalBodyRef = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState<ExpandedTable | null>(null);

    const handleExpand = useCallback((table: HTMLTableElement) => {
        // Clone a clean copy; the modal re-derives colgroup/handles independently.
        const clone = table.cloneNode(true) as HTMLTableElement;
        clone.removeAttribute('data-resizable-ready');
        clone.querySelector('colgroup')?.remove();
        clone
            .querySelectorAll('.fl-col-resize-handle')
            .forEach(el => el.remove());
        setExpanded({ title: findTableTitle(table), html: clone.outerHTML });
    }, []);

    const close = useCallback(() => setExpanded(null), []);

    useResizableTables(ref, [p.html], handleExpand);
    useResizableTables(modalBodyRef, [expanded?.html], handleExpand, true);

    useEffect(() => {
        if (!expanded) {
            return;
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                close();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [expanded, close]);

    const modal = expanded
        ? createPortal(
              <div className="fl-table-modal-overlay" onClick={close}>
                  <div
                      className="fl-table-modal"
                      role="dialog"
                      aria-modal="true"
                      aria-label={expanded.title}
                      onClick={e => e.stopPropagation()}
                  >
                      <div className="fl-table-modal-header">
                          <div className="fl-table-modal-title">
                              {expanded.title}
                          </div>
                          <button
                              type="button"
                              className="fl-table-modal-close"
                              aria-label="Close expanded table"
                              onClick={close}
                          >
                              <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                              >
                                  <path
                                      d="M18 6 6 18M6 6l12 12"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                  />
                              </svg>
                          </button>
                      </div>
                      <div
                          ref={modalBodyRef}
                          className="fl-table-modal-body"
                          dangerouslySetInnerHTML={{ __html: expanded.html }}
                      />
                  </div>
              </div>,
              document.body
          )
        : null;

    return (
        <>
            <div
                ref={ref}
                className={p.className}
                style={p.style}
                dangerouslySetInnerHTML={{ __html: p.html }}
            />
            {modal}
        </>
    );
};
